import { apiService } from "../../../utils/api/api.service";
import { requestWithErrorHandling } from "../../../utils/error/handle.error";
import { TokenManager } from "../../../utils/storage/tokenmanager.storage";
import {
  LoginRequest,
  RegistrationRequest,
  UpdateProfileData,
} from "../interfaces/auth.interface";

class AuthService {
  private static instance: AuthService;

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  login = (credentials: LoginRequest) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<any>(`/auth/login`, credentials);
    });
  };

  register = (data: RegistrationRequest) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post<any>(`/auth/register`, data);
    });
  };

  verifyEmail = (data: { email: string; verificationCode: string }) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/verify-email`, data);
    });
  };

  resendVerificationCode = (email: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/resend-verification`, { email });
    });
  };

  // --- Email change (signed-in users) ---------------------------------------
  requestEmailChange = (newEmail: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/change-email/request`, {
        newEmail,
      });
    });
  };

  resendEmailChangeCode = () => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/change-email/resend`, {});
    });
  };

  verifyEmailChange = (verificationCode: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/change-email/verify`, {
        verificationCode,
      });
    });
  };

  cancelEmailChange = () => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/change-email/cancel`, {});
    });
  };

  socialLogin = (provider: string, token: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/social-login`, { provider, token });
    });
  };

  refreshToken = (refreshToken: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/refresh`, { refreshToken });
    });
  };

  updateProfile = (data: UpdateProfileData) => {
    return requestWithErrorHandling(async () => {
      return await apiService.put(`/auth/profile`, data);
    });
  };

  uploadProfileImage = (file: File) => {
    return requestWithErrorHandling(async () => {
      const formData = new FormData();
      formData.append("profileImage", file);
      return await apiService.post(`/auth/profile/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 30000, // 30 seconds for file uploads
      });
    });
  };

  updatePreferences = (preferences: string[]) => {
    return requestWithErrorHandling(async () => {
      return await apiService.put(`/auth/preferences`, { preferences });
    });
  };

  deleteAccount = (payload?: { reason: string; description?: string; action?: 'delete' | 'deactivate' }) => {
    return requestWithErrorHandling(async () => {
      if (payload?.action === 'deactivate') {
        return await apiService.patch(`/auth/deactivate-user`, payload);
      }

      return await apiService.delete(`/auth/delete-user`, {
        data: payload,
      });
    });
  };

  getProfile = () => {
    return requestWithErrorHandling(async () => {
      return await apiService.get(`/auth/me`);
      // return await apiService.get(`/auth/${TokenManager.getUser()?.id}/profile`);
    });
  };

  forgotPassword = (email: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/forgot-password`, { email });
    });
  };

  verifyResetCode = (email: string, code: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/verify-reset-code`, { email, code });
    });
  };

  changePassword = (data: any) => {
    return requestWithErrorHandling(async () => {
      return await apiService.put(`/auth/change-password`, data);
    });
  };

  verifyCurrentPassword = (password: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/verify-current-password`, { password });
    });
  };

  resetPassword = (data: any) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/reset-password`, data);
    });
  };

  verifyLoginOTP = (email: string, code: string) => {
    return requestWithErrorHandling(async () => {
      return await apiService.post(`/auth/verify-login-otp`, { email, code });
    });
  };

  logout = () => {
    return requestWithErrorHandling(async () => {
      const refreshToken = TokenManager.getRefreshToken();
      return await apiService.post(`/auth/logout`, { refreshToken });
    });
  };

  getCookiePreferences = () => {
    return requestWithErrorHandling(async () => {
      const res = await apiService.get<any>(`/auth/cookie-preferences`);
      const parsed = this.deserializeCookiePreferences(res?.data?.cookiePreferences ?? null);
      const dataObj = res && res.data && typeof res.data === 'object' ? res.data : {};
      return { ...res, data: { ...dataObj, cookiePreferences: parsed } };
    });
  };

  private serializeCookiePreferences(preferences: any): string {
    // Store as JSON string
    return JSON.stringify(preferences);
  }

  private deserializeCookiePreferences(preferences: string | null): any {
    try {
      if (!preferences) return null;
      return JSON.parse(preferences);
    } catch (error) {
      console.error('Failed to parse cookie preferences:', error);
      return null;
    }
  }

  updateCookiePreferences = (preferences: any) => {
    return requestWithErrorHandling(async () => {
      // If preferences is a simple string flag ('all'|'none'|'essential'), send it as-is.
      // Otherwise send a JSON string.
      const bodyPreferences = typeof preferences === 'string' ? preferences : this.serializeCookiePreferences(preferences);
      return await apiService.put(`/auth/cookie-preferences`, {
        preferences: bodyPreferences,
      });
    });
  };
}

export const authService = AuthService.getInstance();
